"use client";

import { useEffect, useState, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useBreakpoint } from '@/utils/useBreakpoint';

interface StickySectionProps {
  children: React.ReactNode;
  height: string;
  mobileHeight: string;
  transitionTime?: number;
  isFallback?: boolean;
}

const StickySection: React.FC<StickySectionProps> = ({ 
  children, 
  height,
  mobileHeight,
  transitionTime = 0,
  isFallback = false,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSnapping, setIsSnapping] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 'mobile';
  
  const enableScrollSnapping = !isFallback && isMobile;
  
  useEffect(() => {
    // Reset the loaded state when the component mounts or pathname changes
    setIsLoaded(false);
    
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      setIsLoaded(true);
      
      // Add data attributes to sections for scroll snapping
      if (containerRef.current && !isFallback) {
        const sections = containerRef.current.querySelectorAll('section');
        sections.forEach((section, index) => {
          section.setAttribute('data-scroll-position', index.toString());
        });
      }
    }, 100);
    
    return () => {
      clearTimeout(timer);
    };
  }, [pathname, isFallback]); // Rerun when pathname or fallback state changes
  
  // Handle scroll snapping for mobile
  useEffect(() => {
    if (!enableScrollSnapping || !isLoaded) return;
    
    let lastScrollY = window.scrollY;
    let ticking = false;
    
    const handleScroll = () => {
      if (isSnapping || ticking) return;
      
      const currentScrollY = window.scrollY;
      const scrollDirection = currentScrollY > lastScrollY ? 'down' : 'up';
      const scrollDelta = Math.abs(currentScrollY - lastScrollY);
      
      // Only snap if the scroll delta is significant
      if (scrollDelta > 10) {
        ticking = true;
        
        // Find all sections with data attributes
        const sections = document.querySelectorAll('section[data-scroll-position]');
        if (sections.length === 0) {
          ticking = false;
          return;
        }
        
        setIsSnapping(true);
        
        // Calculate the current visible section based on viewport
        const viewportHeight = window.innerHeight;
        const viewportCenter = currentScrollY + viewportHeight / 2;
        
        // Find the current section and the next/previous section to scroll to
        let targetScrollY = 0;
        let minDistance = Infinity;
        let nearestSection: Element | null = null;
        
        // First find the nearest section
        sections.forEach((section) => {
          const rect = section.getBoundingClientRect();
          const sectionTop = rect.top + window.scrollY;
          const sectionCenter = sectionTop + rect.height / 2;
          const distance = Math.abs(viewportCenter - sectionCenter);
          
          if (distance < minDistance) {
            minDistance = distance;
            nearestSection = section;
          }
        });
        
        // Then find the next or previous section based on scroll direction
        if (nearestSection) {
          const currentIndex = parseInt((nearestSection as Element).getAttribute('data-scroll-position') || '0');
          const targetIndex = scrollDirection === 'down' ? currentIndex + 1 : currentIndex - 1;
          
          // Find the target section
          const targetSection = document.querySelector(`section[data-scroll-position="${targetIndex}"]`);
          
          if (targetSection) {
            // Scroll to the target section
            const targetRect = targetSection.getBoundingClientRect();
            targetScrollY = targetRect.top + window.scrollY;
            
            window.scrollTo({
              top: targetScrollY,
              behavior: 'smooth'
            });
          } else {
            // If no target section found, stay at current section
            const currentRect = (nearestSection as Element).getBoundingClientRect();
            targetScrollY = currentRect.top + window.scrollY;
            
            window.scrollTo({
              top: targetScrollY,
              behavior: 'smooth'
            });
          }
        }
        
        // Reset snapping state after animation
        setTimeout(() => {
          setIsSnapping(false);
          ticking = false;
        }, 800);
      }
      
      lastScrollY = currentScrollY;
    };
    
    // Throttle scroll events
    const throttledScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', throttledScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', throttledScroll);
    };
  }, [enableScrollSnapping, isLoaded, isSnapping]);
  
  // Prevent default scroll during snapping
  useEffect(() => {
    if (!enableScrollSnapping) return;
    
    const preventDefaultScroll = (e: TouchEvent | WheelEvent) => {
      if (isSnapping) {
        e.preventDefault();
      }
    };
    
    window.addEventListener('wheel', preventDefaultScroll as EventListener, { passive: false });
    window.addEventListener('touchmove', preventDefaultScroll as EventListener, { passive: false });
    
    return () => {
      window.removeEventListener('wheel', preventDefaultScroll as EventListener);
      window.removeEventListener('touchmove', preventDefaultScroll as EventListener);
    };
  }, [enableScrollSnapping, isSnapping]);

  return (
    <div 
      key={pathname} // Force remount when path changes
      className={`relative ${isFallback ? '' : 'lg:h-[var(--desktop-height)] h-[var(--mobile-height)]'}`}
      style={isFallback ? {} : { 
        ['--desktop-height' as string]: height,
        ['--mobile-height' as string]: mobileHeight,
        transition: transitionTime ? `all ${transitionTime}ms ease-in-out` : undefined,
      } as React.CSSProperties}
    >
      <div 
        ref={containerRef}
        style={{ 
          position: isFallback ? 'relative' : 'sticky',
          top: isFallback ? 'auto' : 0,
          height: isFallback ? 'auto' : '100vh',
          overflow: isFallback ? 'visible' : 'hidden',
          opacity: isLoaded ? 1 : 0,
          transition: 'opacity 500ms ease-in-out'
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default StickySection;